import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import {
  allocateReceipt, createReceipt, deleteAllocation, deleteReceipt, dismissReceipt,
  downloadReconCsv, freezeMonth, getReconItems, getReconMonth, getReceipts, getWallet,
  importReceipts, importReconList, patchReconItems, refreshMonth, setPeriodStatus,
  syncWallet, updateReconRow, updateWallet,
  type ExcludeReason, type ManualStatus, type Receipt, type ReceiptScope,
  type ReconItem, type ReconRow,
} from '@/api/reconciliation';

// ---------------------------------------------------------------- helpers

const REASONS: { value: ExcludeReason; label: string }[] = [
  { value: 'no_kpi', label: 'Не добил KPI / миндеп' },
  { value: 'fraud', label: 'Фрод' },
  { value: 'duplicate', label: 'Дубль' },
  { value: 'bonus_abuse', label: 'Бонус-абуз' },
  { value: 'chargeback', label: 'Чарджбек' },
  { value: 'not_found', label: 'Рекл не видит клик' },
  { value: 'other', label: 'Другое' },
];
const REASON_LABEL = Object.fromEntries(REASONS.map((r) => [r.value, r.label]));

const MANUAL_STATUSES: { value: ManualStatus; label: string }[] = [
  { value: 'awaiting_check', label: 'Ожидаем сверки' },
  { value: 'checked_awaiting_payment', label: 'Сверено, ждём оплату' },
  { value: 'wont_be_paid', label: 'Не будет оплачено' },
];
const STATUS_LABEL: Record<string, string> = {
  ...Object.fromEntries(MANUAL_STATUSES.map((s) => [s.value, s.label])),
  paid: 'Оплачено',
  partially_paid: 'Оплачено частично',
};
const STATUS_TONE: Record<string, string> = {
  awaiting_check: 'bg-surface-muted text-content-muted',
  checked_awaiting_payment: 'bg-primary/10 text-primary',
  partially_paid: 'bg-warning/10 text-warning',
  paid: 'bg-success/10 text-success',
  wont_be_paid: 'bg-danger/10 text-danger',
};

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

function money(cents: number | null | undefined) {
  if (cents == null) return '—';
  const sign = cents < 0 ? '−' : '';
  return `${sign}$${(Math.abs(cents) / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function signedMoney(cents: number) {
  if (!cents) return money(0);
  return `${cents > 0 ? '+' : ''}${money(cents)}`.replace('+−', '−');
}
function monthLabel(month: string) {
  return `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
}
function shiftMonth(month: string, delta: number) {
  const total = Number(month.slice(0, 4)) * 12 + (Number(month.slice(5, 7)) - 1) + delta;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}
function previousMonth() {
  const now = new Date();
  return shiftMonth(now.toISOString().slice(0, 7), -1);
}

function Badge({ children, tone = 'bg-surface-muted text-content-muted' }: { children: React.ReactNode; tone?: string }) {
  return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{children}</span>;
}

// ------------------------------------------------------------- items drawer

interface DrawerProps {
  month: string;
  row: ReconRow;
  frozen: boolean;
  onClose: () => void;
  onChanged: () => void;
}

function ItemsDrawer({ month, row, frozen, onClose, onChanged }: DrawerProps) {
  const canEdit = useCanEdit();
  const pushToast = useToastStore((s) => s.push);
  const [filter, setFilter] = useState<'' | '0' | '1'>('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState<ExcludeReason>('no_kpi');
  const [note, setNote] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['recon-items', month, row.partner_id, filter, search],
    queryFn: () => getReconItems(month, {
      partner_id: row.partner_id,
      excluded: filter || undefined,
      search: search || undefined,
      limit: 1000,
    }),
  });
  const items = data?.items ?? [];

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allShownSelected = items.length > 0 && items.every((i) => selected.has(i.id));

  const apply = useMutation({
    mutationFn: (excluded: boolean) => patchReconItems(month, {
      ids: [...selected], excluded, reason: excluded ? reason : undefined, note: note || undefined,
    }),
    onSuccess: (res) => {
      pushToast(`Обновлено строк: ${res.changed}`);
      setSelected(new Set());
      setNote('');
      refetch();
      onChanged();
    },
    onError: (e: any) => pushToast(e?.message || 'Не удалось сохранить'),
  });

  return (
    <Modal isOpen onClose={onClose} title={`${row.partner_name} · ${monthLabel(month)}`} size="5xl">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <Stat label="FTD" value={`${row.ftd_count} · ${money(row.ftd_cents)}`} />
          <Stat label="Исключено" value={`${row.excluded_count} · ${money(row.excluded_cents)}`} />
          <Stat label="Квал" value={`${row.qual_count} · ${money(row.qual_cents)}`} />
          <Stat label="Апрув рекла" value={row.approved_cents == null ? '—' : money(row.approved_cents)} />
        </div>

        {!frozen && (
          <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-sm text-content-muted">
            Месяц не заморожен — клик-айди появятся после заморозки. До неё цифры живые и меняются
            с каждым поздним постбеком.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input w-56"
            placeholder="Поиск по click id, бренду, гео"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            value={filter}
            onChange={(v) => setFilter(v as '' | '0' | '1')}
            options={[
              { value: '', label: 'Все' },
              { value: '0', label: 'Только квал' },
              { value: '1', label: 'Только исключённые' },
            ]}
            size="sm"
            className="w-44"
          />
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => void downloadReconCsv(month, row.partner_id, row.partner_name)}>
              Экспорт для рекла
            </Button>
            {canEdit && frozen && (
              <Button size="sm" onClick={() => setImportOpen(true)}>Импорт среза</Button>
            )}
          </div>
        </div>

        {canEdit && frozen && selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-muted p-3">
            <span className="text-sm text-content-muted">Выбрано: {selected.size}</span>
            <Select
              value={reason}
              onChange={(v) => setReason(v as ExcludeReason)}
              options={REASONS}
              size="sm"
              className="w-56"
            />
            <input
              className="input w-52"
              placeholder="Комментарий (необязательно)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Button size="sm" loading={apply.isPending} onClick={() => apply.mutate(true)}>Исключить</Button>
            <Button size="sm" variant="secondary" loading={apply.isPending} onClick={() => apply.mutate(false)}>
              Вернуть в квал
            </Button>
          </div>
        )}

        <div className="max-h-[45vh] overflow-auto rounded-md border border-border">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-surface-muted text-xs uppercase text-content-subtle">
              <tr>
                <th className="w-8 px-2 py-2">
                  <input
                    type="checkbox"
                    checked={allShownSelected}
                    onChange={() => setSelected(allShownSelected ? new Set() : new Set(items.map((i) => i.id)))}
                    className="rounded border-border-strong text-primary focus:ring-primary"
                  />
                </th>
                <th className="px-2 py-2 text-left">Click ID</th>
                <th className="px-2 py-2 text-left">Дата</th>
                <th className="px-2 py-2 text-left">Гео</th>
                <th className="px-2 py-2 text-left">Бренд</th>
                <th className="px-2 py-2 text-right">Выплата</th>
                <th className="px-2 py-2 text-left">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading && <tr><td colSpan={7} className="px-2 py-6 text-center text-content-subtle">Загрузка…</td></tr>}
              {!isLoading && !items.length && (
                <tr><td colSpan={7} className="px-2 py-6 text-center text-content-subtle">Нет строк</td></tr>
              )}
              {items.map((item: ReconItem) => (
                <tr key={item.id} className={item.excluded ? 'bg-danger/5' : undefined}>
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={() => toggle(item.id)}
                      className="rounded border-border-strong text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">{item.click_id}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-content-muted">{item.converted_at?.slice(0, 16) ?? '—'}</td>
                  <td className="px-2 py-1.5">{item.country ?? '—'}</td>
                  <td className="px-2 py-1.5">{item.offer_brand ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right">{money(item.payout_cents_frozen)}</td>
                  <td className="px-2 py-1.5">
                    {item.excluded
                      ? <span className="text-danger" title={item.exclude_note ?? undefined}>
                          {REASON_LABEL[item.exclude_reason ?? 'other'] ?? 'Исключён'}
                        </span>
                      : <span className="text-success">Квал</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && data.total > items.length && (
          <p className="text-xs text-content-subtle">Показано {items.length} из {data.total}. Уточните поиск.</p>
        )}
      </div>

      {importOpen && (
        <ImportDialog
          month={month}
          partnerId={row.partner_id}
          partnerName={row.partner_name}
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); refetch(); onChanged(); }}
        />
      )}
    </Modal>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs uppercase tracking-wide text-content-subtle">{label}</div>
      <div className="mt-0.5 text-base font-semibold text-content">{value}</div>
      {hint && <div className="text-xs text-content-subtle">{hint}</div>}
    </div>
  );
}

// ------------------------------------------------------------ import dialog

function ImportDialog({ month, partnerId, partnerName, onClose, onDone }: {
  month: string; partnerId: string; partnerName: string; onClose: () => void; onDone: () => void;
}) {
  const pushToast = useToastStore((s) => s.push);
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'keep_only' | 'exclude'>('keep_only');
  const [reason, setReason] = useState<ExcludeReason>('no_kpi');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<null | Awaited<ReturnType<typeof importReconList>>>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      importReconList(month, { partner_id: partnerId, text, mode, reason, note: note || undefined, dry_run: dryRun }),
    onSuccess: (res, dryRun) => {
      if (dryRun) { setPreview(res); return; }
      pushToast(`Исключено ${res.excluded}, возвращено ${res.restored}`);
      onDone();
    },
    onError: (e: any) => pushToast(e?.message || 'Не удалось импортировать'),
  });

  return (
    <Modal isOpen onClose={onClose} title={`Импорт среза · ${partnerName}`} size="2xl">
      <div className="space-y-3">
        <div className="rounded-md border border-border bg-surface-muted p-3 text-sm text-content-muted">
          Вставьте список click id из письма или выгрузки рекла. Берётся первая колонка каждой
          строки, заголовок и дубли отбрасываются.
        </div>
        <Select
          value={mode}
          onChange={(v) => { setMode(v as 'keep_only' | 'exclude'); setPreview(null); }}
          options={[
            { value: 'keep_only', label: 'Это список того, что рекл ОПЛАЧИВАЕТ — остальное исключить' },
            { value: 'exclude', label: 'Это список СРЕЗА — исключить перечисленное' },
          ]}
        />
        <textarea
          className="input h-40 w-full font-mono text-xs"
          placeholder={'click_id\nabc123\ndef456'}
          value={text}
          onChange={(e) => { setText(e.target.value); setPreview(null); }}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          <Select value={reason} onChange={(v) => setReason(v as ExcludeReason)} options={REASONS} />
          <input className="input" placeholder="Комментарий к исключениям" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        {preview && (
          <div className="rounded-md border border-border p-3 text-sm">
            <div>В списке: <b>{preview.listed}</b></div>
            <div>Будет исключено: <b className="text-danger">{preview.excluded}</b></div>
            <div>Будет возвращено в квал: <b className="text-success">{preview.restored}</b></div>
            <div>Нет у нас в периоде: <b>{preview.not_found_count}</b></div>
            {preview.not_found.length > 0 && (
              <div className="mt-1 max-h-24 overflow-auto font-mono text-[11px] text-content-subtle">
                {preview.not_found.join(', ')}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button variant="secondary" loading={run.isPending} disabled={!text.trim()} onClick={() => run.mutate(true)}>
            Проверить
          </Button>
          <Button loading={run.isPending} disabled={!preview} onClick={() => run.mutate(false)}>
            Применить
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------- row editor

function RowEditor({ month, row, onClose, onSaved }: {
  month: string; row: ReconRow; onClose: () => void; onSaved: () => void;
}) {
  const pushToast = useToastStore((s) => s.push);
  const [status, setStatus] = useState<ManualStatus>(
    (MANUAL_STATUSES.some((s) => s.value === row.status) ? row.status : 'awaiting_check') as ManualStatus,
  );
  const [statusReason, setStatusReason] = useState(row.status_reason ?? '');
  const [approvedCents, setApprovedCents] = useState(
    row.approved_cents == null ? '' : (row.approved_cents / 100).toFixed(2),
  );
  const [approvedCount, setApprovedCount] = useState(row.approved_count == null ? '' : String(row.approved_count));
  const [note, setNote] = useState(row.note ?? '');

  const save = useMutation({
    mutationFn: () => updateReconRow(month, row.partner_id, {
      status,
      status_reason: statusReason || null,
      approved_cents: approvedCents.trim() === '' ? null : approvedCents.trim(),
      approved_count: approvedCount.trim() === '' ? null : Number(approvedCount),
      note: note || null,
    }),
    onSuccess: () => { pushToast('Сохранено'); onSaved(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось сохранить'),
  });

  return (
    <Modal isOpen onClose={onClose} title={`Сверка · ${row.partner_name}`} size="lg">
      <div className="space-y-3">
        <div className="rounded-md border border-border bg-surface-muted p-3 text-sm text-content-muted">
          Ждём по трекеру: <b className="text-content">{money(row.expected_cents)}</b> ({row.qual_count} квал).
          {row.paid_cents > 0 && <> Пришло: <b className="text-content">{money(row.paid_cents + row.fee_cents)}</b>.</>}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm text-content-muted">Апрувнул рекл, $</span>
            <input className="input w-full" value={approvedCents} onChange={(e) => setApprovedCents(e.target.value)} placeholder="0.00" />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm text-content-muted">Апрувнул депов</span>
            <input className="input w-full" value={approvedCount} onChange={(e) => setApprovedCount(e.target.value)} placeholder="0" />
          </label>
        </div>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Статус</span>
          <Select value={status} onChange={(v) => setStatus(v as ManualStatus)} options={MANUAL_STATUSES} />
          {row.status_is_derived && (
            <span className="mt-1 block text-xs text-content-subtle">
              Сейчас показывается «{STATUS_LABEL[row.status]}» — статус оплаты считается по поступлениям
              и перекрывает выбранный вручную.
            </span>
          )}
        </label>
        {status === 'wont_be_paid' && (
          <label className="block">
            <span className="mb-1 block text-sm text-content-muted">Причина (обязательно)</span>
            <input className="input w-full" value={statusReason} onChange={(e) => setStatusReason(e.target.value)} />
          </label>
        )}
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Заметка</span>
          <input className="input w-full" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>Сохранить</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- receipts

const SOURCE_LABEL: Record<string, string> = { watcher: 'бот', csv: 'CSV', manual: 'руками' };

/** Why a hash on our wallet is not an advertiser paying an invoice. */
const DISMISS_PRESETS = [
  'Внутренний перевод',
  'Возврат',
  'Не от рекла',
  'Тестовый перевод',
];

function DismissDialog({ receipt, onClose, onDone }: {
  receipt: Receipt; onClose: () => void; onDone: () => void;
}) {
  const pushToast = useToastStore((s) => s.push);
  const [reason, setReason] = useState('');

  const save = useMutation({
    mutationFn: () => dismissReceipt(receipt.id, { dismissed: true, reason }),
    onSuccess: () => { pushToast('Помечено как не по сверке'); onDone(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось пометить'),
  });

  return (
    <Modal isOpen onClose={onClose} title="Не относится к сверке" size="lg">
      <div className="space-y-3">
        <p className="text-sm text-content-muted">
          Хеш <span className="font-mono text-xs">{receipt.tx_hash}</span> на{' '}
          <b className="text-content">{money(receipt.amount_cents)}</b> останется в логе поступлений,
          но выйдет из «Не распознано» и из всех сумм сверки. Причина нужна, чтобы через полгода
          было понятно, куда делись деньги.
        </p>
        <div className="flex flex-wrap gap-2">
          {DISMISS_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setReason(p)}
              className={`rounded border px-2 py-1 text-sm transition-colors ${
                reason === p
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border-strong text-content-muted hover:bg-surface-muted'
              }`}
            >{p}</button>
          ))}
        </div>
        <input
          className="input w-full"
          placeholder="Причина"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button loading={save.isPending} disabled={!reason.trim()} onClick={() => save.mutate()}>
            Пометить
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The watched TRC20 address. Read-only by construction: the panel knows one
 * public address and nothing that could move money, so this card holds no
 * secret worth hiding.
 */
function WalletCard({ onChanged }: { onChanged: () => void }) {
  const canEdit = useCanEdit();
  const pushToast = useToastStore((s) => s.push);
  const { data, refetch } = useQuery({ queryKey: ['recon-wallet'], queryFn: getWallet });
  const [address, setAddress] = useState<string | null>(null);
  const value = address ?? data?.address ?? '';

  const save = useMutation({
    mutationFn: (patch: { address?: string; enabled?: boolean }) => updateWallet(patch),
    onSuccess: () => { pushToast('Сохранено'); setAddress(null); refetch(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось сохранить'),
  });
  const [backfillFrom, setBackfillFrom] = useState('');
  const sync = useMutation({
    mutationFn: (from?: string) => syncWallet(from),
    onSuccess: (res) => {
      pushToast(
        res.truncated
          ? `Добавлено ${res.inserted}, но история длиннее — запустите ещё раз с более поздней даты`
          : res.inserted ? `Новых поступлений: ${res.inserted}` : 'Новых поступлений нет',
      );
      refetch();
      onChanged();
    },
    onError: (e: any) => pushToast(e?.message || 'Не удалось опросить сеть'),
  });

  if (!data) return null;

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-content">Кошелёк {data.chain} · {data.asset}</span>
        <Badge tone={data.enabled ? 'bg-success/10 text-success' : 'bg-surface-muted text-content-muted'}>
          {data.enabled ? 'вотчер включён' : 'вотчер выключен'}
        </Badge>
        {data.lastRun && <span className="text-xs text-content-subtle">опрошено {data.lastRun} UTC</span>}
        {!!data.watched_receipts && (
          <span className="text-xs text-content-subtle">
            поймано ботом: {data.watched_receipts} на {money(data.watched_cents)}
          </span>
        )}
        {canEdit && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" loading={sync.isPending} onClick={() => sync.mutate(undefined)}>
              Проверить сейчас
            </Button>
            <Button
              size="sm"
              variant="secondary"
              loading={save.isPending}
              onClick={() => save.mutate({ enabled: !data.enabled })}
            >
              {data.enabled ? 'Выключить' : 'Включить'}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          className="input w-full font-mono text-xs sm:w-96"
          placeholder="TRON-адрес, на который платят реклы"
          value={value}
          disabled={!canEdit}
          onChange={(e) => setAddress(e.target.value)}
        />
        {canEdit && address !== null && address !== data.address && (
          <Button size="sm" loading={save.isPending} onClick={() => save.mutate({ address })}>
            Сохранить адрес
          </Button>
        )}
        <span className="text-xs text-content-subtle">
          опрашивается каждые 30 минут, только входящие USDT
        </span>
      </div>

      {/* The routine window reaches 30 days back on a fresh wallet, so anything
          older than the day the watcher was switched on was never fetched.
          Asking for it is a deliberate action, not a silent widening. */}
      {canEdit && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs text-content-subtle">
            История до включения вотчера не подтягивается сама — заберите её вручную:
          </span>
          <input
            type="date"
            className="input w-40 text-xs"
            value={backfillFrom}
            onChange={(e) => setBackfillFrom(e.target.value)}
          />
          <Button
            size="sm"
            variant="secondary"
            loading={sync.isPending}
            disabled={!backfillFrom}
            onClick={() => sync.mutate(backfillFrom)}
          >
            Подтянуть историю
          </Button>
        </div>
      )}

      {data.lastError && (
        <div className="mt-2 rounded border border-danger/30 bg-danger/5 p-2 text-xs text-danger">
          Последняя ошибка опроса: {data.lastError}
        </div>
      )}
    </div>
  );
}

function ReceiptsTab({ month, rows, unallocatedOnly, onChanged }: {
  month: string; rows: ReconRow[]; unallocatedOnly: boolean; onChanged: () => void;
}) {
  const canEdit = useCanEdit();
  const pushToast = useToastStore((s) => s.push);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [allocFor, setAllocFor] = useState<string | null>(null);
  // Postpay advertisers settle July in August, so the month's own calendar
  // window is the wrong default: it would show July the payments for June.
  const [scope, setScope] = useState<ReceiptScope>('relevant');
  const [showDismissed, setShowDismissed] = useState(false);
  const [dismissTarget, setDismissTarget] = useState<Receipt | null>(null);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['recon-receipts', month, unallocatedOnly, scope, showDismissed],
    queryFn: () => getReceipts({
      month: unallocatedOnly ? undefined : month,
      scope: unallocatedOnly ? undefined : scope,
      unallocated: unallocatedOnly,
      withDismissed: unallocatedOnly && showDismissed,
    }),
  });
  const receipts = data?.receipts ?? [];
  const allocations = data?.allocations ?? [];
  const byMonth = data?.unallocated_by_month ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => deleteReceipt(id),
    onSuccess: () => { pushToast('Удалено'); refetch(); onChanged(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось удалить'),
  });

  return (
    <div className="space-y-3">
      {!unallocatedOnly && <WalletCard onChanged={() => { refetch(); onChanged(); }} />}
      <div className="flex flex-wrap items-center gap-2">
        {unallocatedOnly ? (
          <>
            <p className="text-sm text-content-subtle">
              Поступления с нераспределённым остатком — это и есть неверифицированные суммы.
              Список сквозной, не за выбранный месяц: хеш без привязки не принадлежит ни одному
              периоду, пока вы его туда не отнесёте.
            </p>
            <label className="flex items-center gap-2 text-sm text-content-muted">
              <input
                type="checkbox"
                checked={showDismissed}
                onChange={(e) => setShowDismissed(e.target.checked)}
                className="rounded border-border-strong text-primary focus:ring-primary"
              />
              Показать помеченные «не по сверке»
            </label>
          </>
        ) : (
          <>
            <Select
              value={scope}
              onChange={(v) => setScope(v as ReceiptScope)}
              options={[
                { value: 'relevant', label: `Для сверки за ${monthLabel(month)}` },
                { value: 'arrived', label: `Пришедшие в ${monthLabel(month)}` },
                { value: 'all', label: 'Все поступления' },
              ]}
              size="sm"
              className="w-64"
            />
            <p className="text-sm text-content-subtle">
              {scope === 'relevant'
                ? 'Привязанные к этому месяцу и всё ещё нераспределённое — независимо от даты прихода: за июль платят в августе.'
                : scope === 'arrived'
                  ? 'Календарное окно месяца по дате прихода — обычно это оплата за прошлый месяц.'
                  : 'Все поступления, новые сверху.'}
            </p>
          </>
        )}
        {canEdit && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>Импорт CSV</Button>
            <Button size="sm" onClick={() => setAddOpen(true)}>Добавить поступление</Button>
          </div>
        )}
      </div>

      {/* A single "не распознано" total answers nothing: the first thing anyone
          asks is which months that money came in. Grouped by arrival date —
          the only month a hash nobody has claimed can honestly be filed under. */}
      {unallocatedOnly && !!byMonth.length && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-muted p-3">
          <span className="text-sm text-content-muted">Пришло в:</span>
          {byMonth.map((m) => (
            <span key={m.month} className="rounded bg-surface px-2 py-1 text-sm">
              <span className="text-content">{monthLabel(m.month)}</span>
              <b className="ml-1.5 text-warning">{money(m.cents)}</b>
              <span className="ml-1 text-content-subtle">({m.count})</span>
            </span>
          ))}
          <span className="text-xs text-content-subtle">
            это месяц прихода денег, а не месяц сверки — за июль платят в августе
          </span>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-muted text-xs uppercase text-content-subtle">
            <tr>
              <th className="px-3 py-2 text-left">Дата</th>
              <th className="px-3 py-2 text-left">Хеш</th>
              <th className="px-3 py-2 text-left">Сеть</th>
              <th className="px-3 py-2 text-right">Сумма</th>
              <th className="px-3 py-2 text-right">Распределено</th>
              <th className="px-3 py-2 text-left">Кому</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading && <tr><td colSpan={7} className="px-3 py-6 text-center text-content-subtle">Загрузка…</td></tr>}
            {!isLoading && !receipts.length && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-content-subtle">Поступлений нет</td></tr>
            )}
            {receipts.map((r) => {
              const mine = allocations.filter((a) => a.receipt_id === r.id);
              return (
                <tr
                  key={r.id}
                  className={r.dismissed
                    ? 'opacity-60'
                    : r.unallocated_cents > 0 ? 'bg-warning/5' : undefined}
                >
                  <td className="px-3 py-2 whitespace-nowrap">{r.received_at.slice(0, 16)}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs" title={r.tx_hash}>
                      {r.tx_hash.length > 18 ? `${r.tx_hash.slice(0, 10)}…${r.tx_hash.slice(-6)}` : r.tx_hash}
                    </span>
                    <span className="ml-1.5"><Badge>{SOURCE_LABEL[r.source] ?? r.source}</Badge></span>
                    {!!r.dismissed && (
                      <span className="ml-1" title={`${r.dismiss_reason ?? ''} — ${r.dismissed_by ?? ''}`}>
                        <Badge tone="bg-surface-muted text-content-subtle">не по сверке</Badge>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.chain} · {r.asset}</td>
                  <td className="px-3 py-2 text-right font-medium">{money(r.amount_cents)}</td>
                  <td className="px-3 py-2 text-right">
                    {money(r.allocated_cents)}
                    {r.unallocated_cents > 0 && (
                      <span className="ml-1 text-warning">(остаток {money(r.unallocated_cents)})</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {mine.length
                      ? mine.map((a) => (
                        <span key={a.id} className="mr-1 inline-flex items-center gap-1">
                          <Badge>{a.partner_name} · {a.month} · {money(a.amount_cents + a.fee_cents)}</Badge>
                          {canEdit && (
                            <button
                              className="text-xs text-content-subtle hover:text-danger"
                              onClick={() => void deleteAllocation(a.id).then(() => { refetch(); onChanged(); })}
                            >×</button>
                          )}
                        </span>
                      ))
                      : r.dismissed
                        ? <span className="text-content-subtle">не по сверке: {r.dismiss_reason}</span>
                        : <span className="text-content-subtle">{r.suggested_partner ? `похоже на ${r.suggested_partner}` : '—'}</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canEdit && !r.dismissed && r.unallocated_cents > 0 && (
                      <Button size="sm" variant="secondary" onClick={() => setAllocFor(r.id)}>Привязать</Button>
                    )}
                    {canEdit && !r.dismissed && r.unallocated_cents > 0 && (
                      <button
                        className="ml-2 text-xs text-content-subtle hover:text-content"
                        onClick={() => setDismissTarget(r)}
                        title="Внутренний перевод, возврат, не от рекла — уйдёт из «Не распознано», но останется в логе"
                      >Не по сверке</button>
                    )}
                    {canEdit && !!r.dismissed && (
                      <button
                        className="text-xs text-content-subtle hover:text-content"
                        onClick={() => void dismissReceipt(r.id, { dismissed: false })
                          .then(() => { refetch(); onChanged(); })}
                      >Вернуть в сверку</button>
                    )}
                    {canEdit && (
                      <button
                        className="ml-2 text-xs text-content-subtle hover:text-danger"
                        onClick={() => remove.mutate(r.id)}
                      >Удалить</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {dismissTarget && (
        <DismissDialog
          receipt={dismissTarget}
          onClose={() => setDismissTarget(null)}
          onDone={() => { setDismissTarget(null); refetch(); onChanged(); }}
        />
      )}
      {addOpen && <ReceiptForm onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); refetch(); onChanged(); }} />}
      {importOpen && <ReceiptImport onClose={() => setImportOpen(false)} onSaved={() => { setImportOpen(false); refetch(); onChanged(); }} />}
      {allocFor && (
        <AllocateDialog
          receipt={receipts.find((r) => r.id === allocFor)!}
          month={month}
          rows={rows}
          onClose={() => setAllocFor(null)}
          onSaved={() => { setAllocFor(null); refetch(); onChanged(); }}
        />
      )}
    </div>
  );
}

function ReceiptForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const pushToast = useToastStore((s) => s.push);
  const [form, setForm] = useState({
    tx_hash: '', amount: '', chain: 'TRON', asset: 'USDT',
    from_address: '', to_address: '', received_at: '', note: '',
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => createReceipt(form),
    onSuccess: () => { pushToast('Поступление добавлено'); onSaved(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось сохранить'),
  });

  return (
    <Modal isOpen onClose={onClose} title="Поступление" size="lg">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-sm text-content-muted">Хеш транзакции</span>
          <input className="input w-full font-mono text-xs" value={form.tx_hash} onChange={set('tx_hash')} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Сумма</span>
          <input className="input w-full" value={form.amount} onChange={set('amount')} placeholder="1234.56" />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Дата и время</span>
          <input className="input w-full" value={form.received_at} onChange={set('received_at')} placeholder="2026-07-04 10:00:00" />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Сеть</span>
          <input className="input w-full" value={form.chain} onChange={set('chain')} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Актив</span>
          <input className="input w-full" value={form.asset} onChange={set('asset')} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Отправитель</span>
          <input className="input w-full font-mono text-xs" value={form.from_address} onChange={set('from_address')} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Получатель</span>
          <input className="input w-full font-mono text-xs" value={form.to_address} onChange={set('to_address')} />
        </label>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button loading={save.isPending} disabled={!form.tx_hash || !form.amount} onClick={() => save.mutate()}>
            Сохранить
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ReceiptImport({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const pushToast = useToastStore((s) => s.push);
  const [text, setText] = useState('');
  const save = useMutation({
    mutationFn: () => importReceipts({ text }),
    onSuccess: (res) => {
      pushToast(`Добавлено ${res.inserted}, дублей ${res.duplicates}, пропущено ${res.skipped}`);
      onSaved();
    },
    onError: (e: any) => pushToast(e?.message || 'Не удалось импортировать'),
  });
  return (
    <Modal isOpen onClose={onClose} title="Импорт поступлений" size="2xl">
      <div className="space-y-3">
        <div className="rounded-md border border-border bg-surface-muted p-3 text-sm text-content-muted">
          Вставьте CSV из эксплорера или обменника. Нужны колонки с хешем и суммой; отправитель,
          получатель, сеть и дата подхватываются, если они есть. Повторный импорт того же файла
          ничего не задваивает — хеш уникален.
        </div>
        <textarea
          className="input h-48 w-full font-mono text-xs"
          placeholder={'hash,amount,from,to,date\n0xabc,1200.00,TFrom,TTo,2026-07-04 10:00:00'}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button loading={save.isPending} disabled={!text.trim()} onClick={() => save.mutate()}>Импортировать</Button>
        </div>
      </div>
    </Modal>
  );
}

function AllocateDialog({ receipt, month, rows, onClose, onSaved }: {
  receipt: { id: string; unallocated_cents: number; suggested_partner: string | null };
  month: string; rows: ReconRow[]; onClose: () => void; onSaved: () => void;
}) {
  const pushToast = useToastStore((s) => s.push);
  const suggested = rows.find((r) => r.partner_name === receipt.suggested_partner);
  const [partnerId, setPartnerId] = useState(suggested?.partner_id ?? '');
  const [targetMonth, setTargetMonth] = useState(month);
  const [amount, setAmount] = useState((receipt.unallocated_cents / 100).toFixed(2));
  const [fee, setFee] = useState('');
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: () => allocateReceipt(receipt.id, {
      month: targetMonth, partner_id: partnerId, amount, fee: fee || 0, note: note || undefined,
    }),
    onSuccess: () => { pushToast('Привязано'); onSaved(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось привязать'),
  });

  const monthOptions = useMemo(
    () => [-2, -1, 0, 1].map((d) => shiftMonth(month, d)).map((m) => ({ value: m, label: monthLabel(m) })),
    [month],
  );

  return (
    <Modal isOpen onClose={onClose} title="Привязать поступление" size="lg">
      <div className="space-y-3">
        <p className="text-sm text-content-muted">
          Нераспределённый остаток: <b className="text-content">{money(receipt.unallocated_cents)}</b>.
          Один хеш можно разложить на несколько реклов и месяцев — привязывайте частями.
        </p>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Рекл</span>
          <Select
            value={partnerId}
            onChange={setPartnerId}
            searchable
            options={rows.map((r) => ({ value: r.partner_id, label: r.partner_name }))}
            placeholder="Выберите рекла"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Месяц</span>
          <Select value={targetMonth} onChange={setTargetMonth} options={monthOptions} />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm text-content-muted">Сумма, $</span>
            <input className="input w-full" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm text-content-muted">Комиссия, $</span>
            <input className="input w-full" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="0.00" />
          </label>
        </div>
        <label className="block">
          <span className="mb-1 block text-sm text-content-muted">Заметка</span>
          <input className="input w-full" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button loading={save.isPending} disabled={!partnerId} onClick={() => save.mutate()}>Привязать</Button>
        </div>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------------- page

type Tab = 'recon' | 'receipts' | 'unallocated';

export function Reconciliation() {
  const canEdit = useCanEdit();
  const pushToast = useToastStore((s) => s.push);
  const qc = useQueryClient();
  // Reconciliation is always about a finished month, so the page opens on the
  // previous one rather than the running one.
  const [month, setMonth] = useState(previousMonth());
  const [tab, setTab] = useState<Tab>('recon');
  const [drawerRow, setDrawerRow] = useState<ReconRow | null>(null);
  const [editRow, setEditRow] = useState<ReconRow | null>(null);
  const [onlyOpen, setOnlyOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['recon-month', month],
    queryFn: () => getReconMonth(month),
  });
  const frozen = (data?.period.status ?? 'open') !== 'open';
  const rows = data?.rows ?? [];
  const shown = onlyOpen ? rows.filter((r) => r.status !== 'paid' && r.status !== 'wont_be_paid') : rows;
  const totals = data?.totals;

  const invalidate = () => {
    refetch();
    qc.invalidateQueries({ queryKey: ['recon-receipts'] });
  };

  const freeze = useMutation({
    mutationFn: () => freezeMonth(month),
    onSuccess: (res) => { pushToast(`Месяц заморожен, строк: ${res.items}`); invalidate(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось заморозить'),
  });
  const refresh = useMutation({
    mutationFn: () => refreshMonth(month),
    onSuccess: (res) => { pushToast(`Добавлено ${res.added}, переоценено ${res.repriced}`); invalidate(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось обновить'),
  });
  const close = useMutation({
    mutationFn: () => setPeriodStatus(month, { status: 'closed' }),
    onSuccess: () => { pushToast('Период закрыт'); invalidate(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось закрыть'),
  });
  const reopen = useMutation({
    mutationFn: () => setPeriodStatus(month, { status: 'frozen' }),
    onSuccess: () => { pushToast('Период снова открыт'); invalidate(); },
    onError: (e: any) => pushToast(e?.message || 'Не удалось открыть'),
  });

  const drift = data?.drift;
  const hasDrift = !!drift && (drift.added_count > 0 || drift.changed_count > 0);

  return (
    <div className="space-y-5 pb-28">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-semibold text-content">Сверка</h1>
          <p className="mt-1 text-sm text-content-subtle">
            Месяц по дате конверсии, по всем реклам и сеткам. Квал — это FTD минус то, что рекл срезал.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => setMonth(shiftMonth(month, -1))}>←</Button>
          <span className="min-w-[9rem] text-center text-sm font-medium text-content">{monthLabel(month)}</span>
          <Button size="sm" variant="secondary" onClick={() => setMonth(shiftMonth(month, 1))}>→</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={frozen ? 'bg-primary/10 text-primary' : 'bg-surface-muted text-content-muted'}>
          {data?.period.status === 'closed' ? 'Закрыт'
            : frozen ? `Заморожен ${data?.period.frozen_at?.slice(0, 16) ?? ''}` : 'Не заморожен'}
        </Badge>
        {data?.period.frozen_by && <span className="text-xs text-content-subtle">кем: {data.period.frozen_by}</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => void downloadReconCsv(month)}>Экспорт месяца</Button>
          {canEdit && !frozen && (
            <Button size="sm" loading={freeze.isPending} onClick={() => freeze.mutate()}>Заморозить месяц</Button>
          )}
          {canEdit && frozen && data?.period.status !== 'closed' && (
            <>
              <Button size="sm" variant="secondary" loading={refresh.isPending} onClick={() => refresh.mutate()}>
                Подтянуть изменения
              </Button>
              <Button size="sm" variant="secondary" loading={close.isPending} onClick={() => close.mutate()}>
                Закрыть период
              </Button>
            </>
          )}
          {canEdit && data?.period.status === 'closed' && (
            <Button size="sm" variant="secondary" loading={reopen.isPending} onClick={() => reopen.mutate()}>
              Открыть период
            </Button>
          )}
        </div>
      </div>

      {!frozen && (
        <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-sm text-content-muted">
          Пока месяц не заморожен, цифры живые: поздние постбеки и правки выплат меняют их каждый день.
          Заморозка фиксирует по строке на каждый деп — согласованная сумма после этого не поедет.
        </div>
      )}

      {hasDrift && (
        <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm text-content-muted">
          После заморозки прилетело: <b className="text-content">{drift!.added_count} депов</b> на{' '}
          <b className="text-content">{money(drift!.added_cents)}</b>
          {drift!.changed_count > 0 && <>, переоценено выплат: <b className="text-content">{drift!.changed_count}</b> ({signedMoney(drift!.changed_cents)})</>}.
          Либо перенесите их в следующий месяц, либо нажмите «Подтянуть изменения» и пересогласуйте.
        </div>
      )}

      {!!data?.orphan_deposits.count && (
        <div className="rounded-md border border-border bg-surface-muted p-3 text-sm text-content-muted">
          Депов без рекла: <b className="text-content">{data.orphan_deposits.count}</b> на{' '}
          <b className="text-content">{money(data.orphan_deposits.cents)}</b> — у конверсии нет оффера
          либо партнёра оффера нет в реестре. Их нельзя выставить никому.
        </div>
      )}

      {/* The one pairing that looks like a bug but is not: money is on the
          wallet and the month still says nothing came, because no hash has been
          pointed at a month yet. Say it out loud instead of leaving two numbers
          to argue with each other. */}
      {!!data?.unallocated.cents && totals?.paid_cents === 0 && totals.fee_cents === 0 && (
        <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-sm text-content-muted">
          На кошельке лежит <b className="text-content">{money(data.unallocated.cents)}</b> в{' '}
          {data.unallocated.count} хеш(ах), не привязанных ни к одному реклу и месяцу, — поэтому
          «Пришло за месяц» показывает ноль. Разнесите их на вкладке{' '}
          <button
            type="button"
            className="text-primary underline underline-offset-2"
            onClick={() => setTab('unallocated')}
          >Не распознано</button>.
        </div>
      )}

      <div className="flex gap-1 border-b border-border">
        {([
          ['recon', 'Сверка'],
          ['receipts', 'Поступления'],
          ['unallocated', `Не распознано${data?.unallocated.count ? ` (${data.unallocated.count})` : ''}`],
        ] as [Tab, string][]).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              tab === id ? 'border-b-2 border-primary text-primary' : 'text-content-muted hover:text-content'
            }`}
          >{label}</button>
        ))}
      </div>

      {tab === 'recon' && (
        <>
          <label className="flex items-center gap-2 text-sm text-content-muted">
            <input
              type="checkbox"
              checked={onlyOpen}
              onChange={(e) => setOnlyOpen(e.target.checked)}
              className="rounded border-border-strong text-primary focus:ring-primary"
            />
            Только незакрытые
          </label>

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-muted text-xs uppercase text-content-subtle">
                <tr>
                  <th className="px-3 py-2 text-left">Рекл</th>
                  <th className="px-3 py-2 text-right">FTD</th>
                  <th className="px-3 py-2 text-right">Исключено</th>
                  <th className="px-3 py-2 text-right">Квал</th>
                  <th className="px-3 py-2 text-right">Ждём</th>
                  <th className="px-3 py-2 text-right">Апрув</th>
                  <th className="px-3 py-2 text-right">Пришло</th>
                  <th className="px-3 py-2 text-left">Статус</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {isLoading && <tr><td colSpan={9} className="px-3 py-8 text-center text-content-subtle">Загрузка…</td></tr>}
                {!isLoading && !shown.length && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-content-subtle">За этот месяц депов нет</td></tr>
                )}
                {shown.map((r) => (
                  <tr key={r.partner_id} className="hover:bg-surface-muted">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-content">{r.partner_name}</span>
                        {r.kind === 'network' && <Badge>сетка</Badge>}
                        {r.pay_type === 'prepay' && <Badge tone="bg-primary/10 text-primary">prepay</Badge>}
                        {r.deal_models.includes('spend') && <Badge tone="bg-warning/10 text-warning">spend</Badge>}
                      </div>
                      {r.note && <div className="text-xs text-content-subtle">{r.note}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">{r.ftd_count}<span className="ml-1 text-content-subtle">{money(r.ftd_cents)}</span></td>
                    <td className="px-3 py-2 text-right">
                      {r.excluded_count ? <span className="text-danger">−{r.excluded_count} · {money(r.excluded_cents)}</span> : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{r.qual_count}</td>
                    <td className="px-3 py-2 text-right font-medium">{money(r.expected_cents)}</td>
                    <td className="px-3 py-2 text-right">
                      {r.approved_cents == null ? <span className="text-content-subtle">—</span> : (
                        <>
                          {money(r.approved_cents)}
                          {r.approved_delta_cents !== 0 && (
                            <div className={`text-xs ${r.approved_delta_cents > 0 ? 'text-success' : 'text-danger'}`}>{signedMoney(r.approved_delta_cents)}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.paid_cents || r.fee_cents ? (
                        <>
                          {money(r.paid_cents + r.fee_cents)}
                          {r.paid_delta_cents !== 0 && (
                            <div className={`text-xs ${r.paid_delta_cents > 0 ? 'text-success' : 'text-danger'}`}>{signedMoney(r.paid_delta_cents)}</div>
                          )}
                        </>
                      ) : <span className="text-content-subtle">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                      {r.status === 'wont_be_paid' && r.status_reason && (
                        <div className="text-xs text-content-subtle">{r.status_reason}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="secondary" onClick={() => setDrawerRow(r)}>Клик-айди</Button>
                      {canEdit && frozen && (
                        <Button size="sm" variant="secondary" className="ml-2" onClick={() => setEditRow(r)}>Сверка</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'receipts' && <ReceiptsTab month={month} rows={rows} unallocatedOnly={false} onChanged={invalidate} />}
      {tab === 'unallocated' && <ReceiptsTab month={month} rows={rows} unallocatedOnly onChanged={invalidate} />}

      {totals && (
        <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur md:pl-60">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <span>Ждём по трекеру <b className="text-content">{money(totals.expected_cents)}</b></span>
            <span className="text-content-muted">
              Апрувнули <b className="text-content">{money(totals.approved_cents)}</b>
              {totals.cut_cents !== 0 && <span className={`ml-1 ${totals.cut_cents > 0 ? 'text-success' : 'text-danger'}`}>({signedMoney(totals.cut_cents)})</span>}
            </span>
            {/* Three numbers about THIS month, then one about the wallet as a
                whole. Without "за месяц" the pair reads as a contradiction:
                money is on the wallet, yet the month says nothing came. */}
            <span
              className="text-content-muted"
              title="Поступления, привязанные к этому месяцу. Деньги, лежащие на кошельке без привязки, сюда не попадают."
            >
              Пришло за месяц <b className="text-content">{money(totals.paid_cents + totals.fee_cents)}</b>
              {totals.shortfall_cents !== 0 && <span className={`ml-1 ${totals.shortfall_cents > 0 ? 'text-success' : 'text-danger'}`}>({signedMoney(totals.shortfall_cents)})</span>}
            </span>
            {!!data?.unallocated.cents && (
              <button
                type="button"
                onClick={() => setTab('unallocated')}
                className="text-content-muted underline decoration-dotted underline-offset-4 hover:text-content"
                title="Пришло на кошелёк, но ещё не привязано ни к одному реклу и месяцу — по всем месяцам сразу"
              >
                Не распознано, всего <b className="text-warning">{money(data.unallocated.cents)}</b>
                <span className="ml-1 text-content-subtle">({data.unallocated.count})</span>
              </button>
            )}
            {!!totals.prepay_expected_cents && (
              <span className="text-content-subtle">из них prepay {money(totals.prepay_expected_cents)}</span>
            )}
            <span className="ml-auto text-content-subtle">
              закрыто {totals.partners_settled} из {totals.partners}
            </span>
          </div>
        </div>
      )}

      {drawerRow && (
        <ItemsDrawer
          month={month}
          row={drawerRow}
          frozen={frozen}
          onClose={() => setDrawerRow(null)}
          onChanged={invalidate}
        />
      )}
      {editRow && (
        <RowEditor
          month={month}
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); invalidate(); }}
        />
      )}
    </div>
  );
}
